// FILE: ThreadAssigneesControl.tsx
// Purpose: Chat-header control to share a thread with other users (assign / unassign), with a searchable picker.
// Layer: Header action control
// Depends on: the user directory endpoint, the orchestration dispatch bridge, and the auth store.

import type { ThreadId, UserId } from "@taskin-code/contracts";
import { memo, useCallback, useDeferredValue, useMemo, useState } from "react";
import { UsersIcon } from "~/lib/icons";
import { Button } from "~/components/ui/button";
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxPopup,
  ComboboxTrigger,
} from "~/components/ui/combobox";
import { toastManager } from "~/components/ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { useAuthStore } from "~/auth/authStore";
import { readNativeApi } from "~/nativeApi";
import { useUserDirectory } from "~/lib/userDirectoryReactQuery";
import { newCommandId } from "~/lib/utils";
import { useStore } from "~/store";
import { createThreadSelector } from "~/storeSelectors";
import { UserAvatar } from "./UserAvatar";
import {
  buildThreadAssigneeRows,
  filterThreadAssigneeRows,
  summarizeThreadAssignees,
  type ThreadAssigneeRow,
} from "./ThreadAssigneesControl.logic";

interface ThreadAssigneesControlProps {
  readonly threadId: ThreadId;
}

/** Beyond this many rows the list is windowed instead of fully rendered. */
const VIRTUALIZE_THRESHOLD = 40;

/**
 * Shares a thread with other people.
 *
 * Only rendered when there is a session: an install without auth has one
 * implicit user, so there is nobody to share with and the control would be a
 * dead end.
 *
 * Nothing is applied optimistically. Membership decides *visibility*, and the
 * server is the only authority on it — a local guess that the server then
 * refuses would put a thread in someone's sidebar that they cannot open. The
 * checkmarks move when `thread.user-assigned` / `thread.user-unassigned` come
 * back through the store.
 *
 * ── Why a Combobox and not a Menu ─────────────────────────────────────────
 *
 * This was a `Menu` until the directory started being worth searching. A menu
 * cannot hold a text field: Base UI wires typeahead to the popup, so every
 * keystroke aimed at an input also jumps the highlight. `Combobox` is the part
 * built for "filter a list, then pick from it", and
 * `BranchToolbarBranchSelector` already uses it the same way.
 *
 * Selection stays *ours*, though. The combobox is given the assigned ids as a
 * controlled `value` and never a state setter, so a click cannot move a
 * checkmark on its own — it only dispatches, and the value it re-renders with
 * is whatever the store says. That is what keeps "no optimistic membership"
 * true even though the check indicator is now drawn by Base UI.
 */
export const ThreadAssigneesControl = memo(function ThreadAssigneesControl({
  threadId,
}: ThreadAssigneesControlProps) {
  const currentUser = useAuthStore((state) => state.user);
  const thread = useStore(useMemo(() => createThreadSelector(threadId), [threadId]));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [pendingUserId, setPendingUserId] = useState<UserId | null>(null);

  // Fetched when the popup first opens, not on mount: most sessions never share
  // a thread, and the header renders on every thread switch. The query itself
  // is shared with every other surface that needs the directory, so a popup
  // opened after an avatar was drawn costs no request at all.
  const directoryQuery = useUserDirectory({ enabled: open && currentUser !== null });

  const assignedUserIds = thread?.assignedUserIds;
  const rows = useMemo(
    () =>
      buildThreadAssigneeRows({
        directory: directoryQuery.data ?? [],
        createdBy: thread?.createdBy ?? null,
        assignedUserIds,
        currentUserId: currentUser?.id ?? null,
        currentUserAvatarFileName: currentUser?.avatarFileName,
      }),
    [
      directoryQuery.data,
      thread?.createdBy,
      assignedUserIds,
      currentUser?.id,
      currentUser?.avatarFileName,
    ],
  );
  const visibleRows = useMemo(
    () => filterThreadAssigneeRows(rows, deferredQuery),
    [rows, deferredQuery],
  );

  const rowByUserId = useMemo(() => new Map(rows.map((row) => [row.userId, row])), [rows]);
  const allUserIds = useMemo(() => rows.map((row) => row.userId), [rows]);
  const visibleUserIds = useMemo(() => visibleRows.map((row) => row.userId), [visibleRows]);
  // What the combobox considers selected — read from the store on every render,
  // never written locally.
  const selectedUserIds = useMemo(
    () => rows.filter((row) => row.assigned).map((row) => row.userId),
    [rows],
  );

  const toggle = useCallback(
    async (row: ThreadAssigneeRow) => {
      const api = readNativeApi();
      if (!api) return;
      const share = !row.assigned;
      setPendingUserId(row.userId);
      try {
        await api.orchestration.dispatchCommand({
          type: share ? "thread.assign-user" : "thread.unassign-user",
          commandId: newCommandId(),
          threadId,
          userId: row.userId,
        });
      } catch (error) {
        toastManager.add({
          type: "error",
          title: share ? "Failed to share thread" : "Failed to remove access",
          description: error instanceof Error ? error.message : "An error occurred.",
        });
      } finally {
        setPendingUserId(null);
      }
    },
    [threadId],
  );

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen);
    // A stale query would otherwise greet the next person who opens the picker
    // with a filtered list they did not ask for.
    if (!nextOpen) setQuery("");
  }, []);

  if (!currentUser || !thread) {
    return null;
  }

  const summary = summarizeThreadAssignees(assignedUserIds);
  const sharedCount = assignedUserIds?.length ?? 0;
  const isBusy = pendingUserId !== null;

  return (
    <Combobox
      items={allUserIds}
      filteredItems={visibleUserIds}
      multiple
      value={selectedUserIds}
      open={open}
      onOpenChange={handleOpenChange}
      virtualized={visibleUserIds.length > VIRTUALIZE_THRESHOLD}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <ComboboxTrigger
              render={
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  className="shrink-0 gap-1.5"
                  aria-label="Share thread with users"
                >
                  <UsersIcon className="size-3.5 shrink-0" />
                  {sharedCount > 0 ? (
                    <span className="font-normal tabular-nums">{sharedCount}</span>
                  ) : null}
                </Button>
              }
            />
          }
        />
        <TooltipPopup side="bottom">{summary}</TooltipPopup>
      </Tooltip>
      <ComboboxPopup align="end" className="w-64">
        <div className="border-b p-1">
          <ComboboxInput
            className="rounded-md border-border/60 bg-background shadow-none before:hidden has-focus-visible:border-neutral-500/15 has-focus-visible:ring-0"
            inputClassName="ring-0"
            placeholder="Search users…"
            showTrigger={false}
            size="sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {directoryQuery.isPending ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">Loading users…</div>
        ) : directoryQuery.isError ? (
          <div className="px-2 py-1.5 text-xs text-destructive">Could not load users</div>
        ) : rows.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground">No other users</div>
        ) : (
          <>
            <ComboboxEmpty>No users match</ComboboxEmpty>
            <ComboboxList className="max-h-56">
              {visibleRows.map((row, index) => (
                <ComboboxItem
                  key={row.userId}
                  index={index}
                  value={row.userId}
                  // The owner is not a membership: there is no grant to revoke,
                  // so the row states the fact instead of offering a switch that
                  // would silently do nothing.
                  disabled={row.isOwner || isBusy}
                  onClick={(event) => {
                    // The popup stays open: sharing with three people in a row
                    // is the common case, and each click has to survive a
                    // round-trip before its checkmark moves.
                    event.preventDefault();
                    if (row.isOwner || isBusy) return;
                    void toggle(row);
                  }}
                  className="min-h-7 py-1 sm:text-xs"
                >
                  <span className="flex items-center gap-2">
                    <UserAvatar
                      userId={row.userId}
                      displayName={row.displayName}
                      avatarFileName={row.avatarFileName}
                      size="xs"
                      className="shrink-0"
                    />
                    <span className="truncate">{row.displayName}</span>
                    {row.isOwner ? (
                      <span className="ml-auto pl-2 text-[11px] opacity-60">Owner</span>
                    ) : row.isSelf ? (
                      <span className="ml-auto pl-2 text-[11px] opacity-60">You</span>
                    ) : null}
                  </span>
                </ComboboxItem>
              ))}
            </ComboboxList>
          </>
        )}
      </ComboboxPopup>
    </Combobox>
  );
});
